import { Injectable, OnDestroy } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, of, catchError, switchMap, map, take, tap, finalize, share } from 'rxjs';
import { IMessage, IMessageTimeout, LoginStepResult, MessageType, UserLoginComponent, WhatDo } from '../components/user-login/user-login.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isNil, isPlainObject } from 'lodash-es';
import { DISABLE_UNAUTHINTERCEPTOR } from '../utils/unauthorized.interceptor';
import { AuthService as API } from '../swagger/api/auth.service';
import type { EnhancedUser as IUser, Login1Result } from '../swagger';
@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  readonly #login = new BehaviorSubject<IUser | null>(null);
  readonly #cookie: string;
  readonly context = new HttpContext();
  isLogged(forceUpdate: boolean = false): Observable<IUser | null> {
    const hasCookie = document.cookie.includes(this.#cookie + '=');
    if (forceUpdate) {
      if (!hasCookie) console.warn(`AuthService.isLogged was called with forceUpdate=true but the cookie ${this.#cookie} is not present.`);
    } else {
      const a = this.#login.value;
      if (a) return this.#login.asObservable(); //.pipe(startWith(a));
    }
    if (hasCookie) {
      return (
        forceUpdate ? this.api.authUpdatePost('body', false, { context: this.context }) : this.api.authWhoamiGet('body', false, { context: this.context })
      ).pipe(
        catchError(() => {
          deleteCookie(this.#cookie);
          return of(null);
        }),
        tap(a => this.#login.next(a)),
        switchMap(() => this.#login.asObservable())
      );
    }
    return this.#login.asObservable();
  }

  constructor(
    private readonly modal: NgbModal,
    private readonly api: API
  ) {
    this.context.set(DISABLE_UNAUTHINTERCEPTOR, true);
    this.#cookie = 'OltreCookie';
  }
  private loginStep1(name: string, password: string): Observable<LoginStepResult<IUser>> {
    return this.api
      .authLogin1Post(
        {
          name,
          password
        },
        'body',
        false,
        { context: this.context }
      )
      .pipe(
        map(a => {
          if (isPasswordExpiredResult(a))
            return {
              what: WhatDo.Error,
              messages: [
                {
                  type: MessageType.Link,
                  text: 'La tua password è scaduta. Cambiare la password.',
                  url: `/reset-password/${encodeURIComponent(name)}`
                }
              ]
            };
          if (isLoginNoOtpResult(a)) return { what: WhatDo.Skip, skipPayload: a.user };
          if (isLoginOtpRequiredResult(a)) {
            const messages: IMessage[] = [];
            if (a.expirationDate)
              messages.push({
                type: MessageType.Info,
                text: `La tua password è valida fino al ${new Date(a.expirationDate).toLocaleDateString()}`
              });
            messages.push({
              type: MessageType.Timeout,
              text: 'Il codice OTP scade ',
              date: a.otpExpiresAt
            } as IMessageTimeout);
            return { what: WhatDo.Next, messages };
          }
          throw new Error('Unknown login step 1 result');
        }),
        catchError(() => of({ what: WhatDo.Error }))
      );
  }

  private loginStep2(otpCode: string): Observable<IUser | null> {
    return this.api
      .authLogin2Post(
        {
          otpCode
        },
        'body',
        false,
        { context: this.context }
      )
      .pipe(
        tap(a => this.#login.next(a)),
        catchError(() => of(null))
      );
  }

  logout(): Observable<boolean> {
    if (document.cookie.includes(this.#cookie + '='))
      return this.api.authLogoutGet('body', false, { context: this.context }).pipe(
        map(() => true),
        catchError(() => {
          console.warn('Logout failed, cookie invalidated serverside');
          deleteCookie(this.#cookie);
          return of(false);
        }),
        finalize(() => this.#login.next(null))
      );
    this.#login.next(null);
    return of(false);
  }
  #requestingLogin: Observable<IUser | null> | null = null;
  requestLogin() {
    if (this.#requestingLogin) return this.#requestingLogin;
    return (this.#requestingLogin = this.isLogged().pipe(
      take(1),
      switchMap(a => {
        if (isPlainObject(a)) return of(a);
        return this.showModal().pipe(
          tap(a => this.#login.next(a)),
          catchError(() => of(null)) // in case the modal is dismissed
        );
      }),
      finalize(() => (this.#requestingLogin = null)),
      share()
    ));
  }
  haveRoles(needed: string[]) {
    return this.isLogged().pipe(
      map(a => {
        if (isNil(a) || !isPlainObject(a)) return false;
        if (needed.length === 0) return true;
        if (!Array.isArray(a.roles)) return false;
        return needed.every(b => a.roles.includes(b));
      })
    );
  }
  #modalObs: Observable<IUser | null> | null = null;
  private clearModal(status: boolean) {
    this.#modalObs = null;
    console.log('Login modal ' + (status ? 'closed successfully' : 'cancelled'));
  }
  private showModal() {
    if (this.#modalObs) return this.#modalObs;
    const mod = this.modal.open(UserLoginComponent);
    const ins = mod.componentInstance as UserLoginComponent<IUser>;
    ins.loginStep1 = (name, password) => this.loginStep1(name, password);
    ins.loginStep2 = otp => this.loginStep2(otp);
    ins.actions = [
      {
        type: MessageType.Link,
        text: 'Reset password',
        url: '/reset-password'
      }
    ];
    return (this.#modalObs = mod.closed as Observable<IUser | null>).pipe(
      finalize(() => this.clearModal(true)),
      catchError(err => {
        this.clearModal(false);
        throw err;
      })
    );
  }
  ngOnDestroy() {
    this.#login.complete();
  }
}
function deleteCookie(name: string) {
  document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

interface PasswordExpiredResult extends Login1Result {
  expirationDate: string | null;
}
function isPasswordExpiredResult(result: Login1Result): result is PasswordExpiredResult {
  return result.status === 1;
}

interface LoginNoOtpResult extends Login1Result {
  user: IUser;
}
function isLoginNoOtpResult(result: Login1Result): result is LoginNoOtpResult {
  return result.status === 2;
}

interface LoginOtpRequiredResult extends PasswordExpiredResult {
  otpExpiresAt: string;
}
function isLoginOtpRequiredResult(result: Login1Result): result is LoginOtpRequiredResult {
  return result.status === 3;
}
